import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmFitmentSchemaFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { schemaMessages, sectionMessages } from "../messages";
import { wsmFitmentSchemaAddPath, wsmFitmentSchemaUrl } from "../urls";
import { getFitmentLabel, getSchemaAxes } from "../utils";
import { WsmFitmentLoadError } from "./WsmFitmentLoadError";
import { WsmFitmentSectionLinks } from "./WsmFitmentSectionLinks";

const numberOfColumns = 3;

export interface WsmFitmentSchemaListPageProps {
  schemas: WsmFitmentSchemaFragment[] | undefined;
  /** Set when the list failed to load: the card shows a retry instead of rows. */
  onRetry?: () => void;
}

export const WsmFitmentSchemaListPage = ({ schemas, onRetry }: WsmFitmentSchemaListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.schemas)}>
        <WsmFitmentSectionLinks current="schemas" />
        <Button
          onClick={() => navigate(wsmFitmentSchemaAddPath)}
          data-test-id="wsm-fitment-schema-create"
        >
          {intl.formatMessage(schemaMessages.create)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="wsm-fitment-schema-list">
        <DashboardCard.Content paddingX={0}>
          {onRetry ? (
            <WsmFitmentLoadError onRetry={onRetry} />
          ) : (
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(schemaMessages.colName)}</TableCell>
                  <TableCell>{intl.formatMessage(schemaMessages.colAxes)}</TableCell>
                  <TableCell>{intl.formatMessage(schemaMessages.colFlags)}</TableCell>
                </TableRowLink>
              </TableHead>
              <TableBody>
                {renderCollection(
                  schemas,
                  row => (
                    <TableRowLink
                      key={row ? row.id : "skeleton"}
                      hover={!!row}
                      href={row ? wsmFitmentSchemaUrl(row.id) : undefined}
                      data-test-id="wsm-fitment-schema-row"
                    >
                      <TableCell>
                        {row ? (
                          <>
                            <Text size={3} display="block">
                              {row.name}
                            </Text>
                            {row.displayLabel && (
                              <Text size={2} color="default2" display="block">
                                {row.displayLabel}
                              </Text>
                            )}
                          </>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          getSchemaAxes(row).map(getFitmentLabel).join(" > ") || "-"
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Box display="flex" gap={2}>
                            {!row.isActive && (
                              <Pill
                                color="error"
                                label={intl.formatMessage(schemaMessages.inactive)}
                              />
                            )}
                            {row.showAsFacet && (
                              <Pill color="info" label={intl.formatMessage(schemaMessages.facet)} />
                            )}
                            {row.searchableByKeyword && (
                              <Pill
                                color="info"
                                label={intl.formatMessage(schemaMessages.keyword)}
                              />
                            )}
                          </Box>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="wsm-fitment-schema-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(schemaMessages.empty)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )}
              </TableBody>
            </ResponsiveTable>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmFitmentSchemaListPage;
